
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  HiOutlineArrowUpRight,
  HiOutlineArrowUp,
  HiOutlineScale,
  HiOutlineArrowLongLeft,
} from "react-icons/hi2";

import { darkLogo } from "../../assets";

import {
  navItems,
  FotterText,
  socialMedia,
  aboutUsData,
} from "../../constants";

/* ============================================
   FOOTER - AL NOMAN LAW OFFICE
============================================ */

function Fotter({
  privacyPath,
  termsPath,
}) {
  const { i18n } = useTranslation();

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  const language = isEnglish ? "en" : "ar";

  /* ======================================
     LOCAL TRANSLATIONS
  ====================================== */

  const translations = {
    ar: {
      eyebrow: "مؤسسة النعمان للمحاماة",
      ctaTitle: "هل لديك مسألة قانونية تحتاج إلى دراسة؟",
      ctaDescription:
        "نحن هنا للاستماع إلى استفسارك ومساعدتك في التعرف على الخيارات القانونية المتاحة.",

      contact: "تواصل معنا",
      navigation: "روابط الموقع",
      social: "تابعنا",
      legal: "معلومات قانونية",

      privacy: "سياسة الخصوصية",
      terms: "الشروط والأحكام",

      copyright: "جميع الحقوق محفوظة",
      development: "تصميم وتطوير",
      backToTop: "العودة إلى الأعلى",
    },

    en: {
      eyebrow: "Al Noman Law Office",
      ctaTitle: "Have a Legal Matter That Needs Attention?",
      ctaDescription:
        "We are here to listen to your concerns and help you understand the legal options available.",

      contact: "Contact Us",
      navigation: "Quick Links",
      social: "Follow Us",
      legal: "Legal Information",

      privacy: "Privacy Policy",
      terms: "Terms & Conditions",

      copyright: "All Rights Reserved",
      development: "Designed & Developed by",
      backToTop: "Back to Top",
    },
  };

  const content = translations[language];

  const footerDescription = isEnglish
    ? FotterText.textEn
    : FotterText.textAr;

    const contactPath = "https://wa.me/967777332887";

  const legalLinks = [
    {
      label: content.privacy,
      path: privacyPath,
    },
    {
      label: content.terms,
      path: termsPath,
    },
  ];

  /* ======================================
     HELPERS
  ====================================== */

  const handleNavigate = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleBackToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer
      className="
        relative isolate
        w-full overflow-hidden

        border-t border-white/10

        bg-[#122B3D]
        text-[#F4F7FA]

        dark:bg-[#081927]

        transition-colors duration-300
      "
    >
      {/* ======================================
          BACKGROUND DETAILS
      ====================================== */}

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute inset-0

          bg-[radial-gradient(ellipse_at_top_right,rgba(108,149,169,0.12),transparent_55%)]
        "
      />

      {/* Small golden accent */}
      <div
        aria-hidden="true"
        className="
          absolute inset-x-0 top-0
          h-px
          bg-gradient-to-r
          from-transparent
          via-[#C19B60]/50
          to-transparent
        "
      />

      {/* ======================================
          TOP CONTACT STRIP
      ====================================== */}

      <div
        className="
          relative
          border-b border-white/10
          bg-white/[0.035]
        "
      >
        <div
          className="
            container

            flex flex-col
            gap-7 py-10

            md:flex-row
            md:items-center
            md:justify-between
            md:py-12
          "
        >
          {/* Text */}
          <div className="max-w-[730px]">

            <div className="mb-4 flex items-center gap-3">

              <span
                aria-hidden="true"
                className="
                  h-px w-8
                  bg-[#D7B77F]
                "
              />

              <span
                className="
                  eyebrow
                  text-[#D7B77F]
                "
              >
                {content.eyebrow}
              </span>

            </div>

            <h2
              className="
                h4 text-white
              "
            >
              {content.ctaTitle}
            </h2>

            <p
              className="
                body-2
                mt-3
                max-w-[650px]
                text-[#B8C9D6]
              "
            >
              {content.ctaDescription}
            </p>

          </div>

          {/* Contact button */}
     
<a
  href={contactPath}
  target="_blank"
  rel="noopener noreferrer"
  className="
    group inline-flex
    min-h-[52px]
    shrink-0 items-center
    justify-center gap-4
    self-start rounded-xl
    border border-white/20
    bg-white/[0.08]
    px-6 py-3 text-white
    backdrop-blur-xl
    transition-all duration-300

    hover:border-white/35
    hover:bg-white/[0.14]

    focus-visible:outline-none
    focus-visible:ring-2
    focus-visible:ring-[#D7B77F]
  "
>
  <span className="button-sm">
    {content.contact}
  </span>

  <HiOutlineArrowUpRight
    size={19}
    aria-hidden="true"
    className="directional-arrow text-[#D7B77F]"
  />
</a>

        </div>
      </div>

      {/* ======================================
          MAIN FOOTER
      ====================================== */}

      <div className="container relative">

        <div
          className="
            grid gap-x-10 gap-y-12

            py-14
            sm:grid-cols-2
            lg:grid-cols-12
            lg:gap-x-8
            lg:py-20
          "
        >
          {/* ==================================
              BRAND / ABOUT
          ================================== */}

          <div
            className="
              sm:col-span-2
              lg:col-span-4
            "
          >
            {/* Logo */}
            <Link
              to="/"
              onClick={handleNavigate}
              aria-label={content.eyebrow}
              className="
                inline-flex
                items-center

                rounded-lg

                focus-visible:outline-none
                focus-visible:ring-2
                focus-visible:ring-[#D7B77F]
              "
            >
              <img
                src={darkLogo}
                alt={content.eyebrow}
                loading="lazy"
                className="
                  block
                  h-auto
                  max-h-[65px]
                  w-auto
                  max-w-[215px]
                  object-contain
                "
              />
            </Link>

            {/* About text */}
            <p
              className="
                body-2
                mt-7
                max-w-[390px]
                text-[#B8C9D6]
              "
            >
              {footerDescription}
            </p>

            {/* Brand signature */}
            <div className="mt-8 flex items-center gap-3">

              <div
                className="
                  flex h-10 w-10
                  items-center justify-center

                  rounded-xl
                  border border-white/10
                  bg-white/[0.05]

                  text-[#D7B77F]
                "
              >
                <HiOutlineScale
                  size={23}
                  aria-hidden="true"
                />
              </div>

              <div
                aria-hidden="true"
                className="
                  h-px w-12
                  bg-[#D7B77F]/60
                "
              />

              <span
                className="
                  font-cairo
                  text-xs font-semibold
                  tracking-wide
                  text-white/50
                "
              >
                AL NOMAN
              </span>

            </div>
          </div>

          {/* ==================================
              NAVIGATION LINKS
          ================================== */}

          <div className="lg:col-span-3">

            <h3
              className="
                h6 mb-7 text-white
              "
            >
              {content.navigation}
            </h3>

            <div
              aria-hidden="true"
              className="
                mb-7 h-px w-10
                bg-[#D7B77F]/70
              "
            />

            <nav
              aria-label={content.navigation}
            >
              <ul className="space-y-4">

                {navItems.map((item, index) => (
                  <li
                    key={item.id ?? index}
                  >
                    <Link
                      to={item.link}
                      onClick={handleNavigate}
                      className="
                        group inline-flex
                        items-center gap-3

                        font-cairo
                        text-sm font-medium
                        leading-7

                        text-[#B8C9D6]

                        transition-colors duration-200

                        hover:text-white

                        focus-visible:outline-none
                        focus-visible:text-[#D7B77F]
                      "
                    >
                      <span
                        aria-hidden="true"
                        className="
                          h-1.5 w-1.5
                          shrink-0
                          rounded-full

                          bg-white/25

                          transition-colors duration-200

                          group-hover:bg-[#D7B77F]
                        "
                      />

                      <span>
                        {isEnglish
                          ? item.titleEn
                          : item.titleAr}
                      </span>

                    </Link>
                  </li>
                ))}

              </ul>
            </nav>

          </div>

          {/* ==================================
              SOCIAL MEDIA
          ================================== */}

          <div className="lg:col-span-3">

            <h3 className="h6 mb-7 text-white">
              {content.social}
            </h3>

            <div
              aria-hidden="true"
              className="
                mb-7 h-px w-10
                bg-[#D7B77F]/70
              "
            />

            <ul className="space-y-3">

              {socialMedia.map((item, index) => {
                const title = isEnglish
                  ? item.titleEn
                  : item.titleAr;

                return (
                  <li key={item.id ?? index}>

                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={title}
                      className="
                        group flex
                        w-fit items-center
                        gap-3

                        rounded-lg

                        text-[#B8C9D6]

                        transition-colors duration-200

                        hover:text-white

                        focus-visible:outline-none
                        focus-visible:ring-2
                        focus-visible:ring-[#D7B77F]
                      "
                    >
                      {/* Social icon */}
                      <span
                        aria-hidden="true"
                        className="
                          flex h-10 w-10
                          shrink-0 items-center
                          justify-center

                          rounded-xl
                          border border-white/10
                          bg-white/[0.05]

                          text-[#D7B77F]

                          transition-all duration-300

                          group-hover:border-white/20
                          group-hover:bg-white/[0.10]

                          [&>svg]:h-5
                          [&>svg]:w-5
                        "
                      >
                        {item.icon}
                      </span>

                      <span
                        className="
                          font-cairo
                          text-sm font-medium
                        "
                      >
                        {title}
                      </span>

                    </a>

                  </li>
                );
              })}

            </ul>
          </div>

          {/* ==================================
              LEGAL LINKS
          ================================== */}

          <div className="lg:col-span-2">

            <h3 className="h6 mb-7 text-white">
              {content.legal}
            </h3>

            <div
              aria-hidden="true"
              className="
                mb-7 h-px w-10
                bg-[#D7B77F]/70
              "
            />

            <ul className="space-y-4">

              {legalLinks.map((item, index) => (
                <li key={index}>

                  {item.path ? (
                    <Link
                      to={item.path}
                      onClick={handleNavigate}
                      className="
                        inline-flex
                        font-cairo
                        text-sm font-medium
                        leading-7

                        text-[#B8C9D6]

                        transition-colors duration-200

                        hover:text-white

                        focus-visible:outline-none
                        focus-visible:text-[#D7B77F]
                      "
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <span
                      className="
                        font-cairo
                        text-sm font-medium
                        leading-7
                        text-white/45
                      "
                    >
                      {item.label}
                    </span>
                  )}

                </li>
              ))}

            </ul>
          </div>
        </div>

        {/* ======================================
            COPYRIGHT / BOTTOM FOOTER
        ====================================== */}

        <div
          className="
            flex flex-col
            gap-5

            border-t border-white/10

            py-6

            md:flex-row
            md:items-center
            md:justify-between
          "
        >
          {/* Copyright */}
          <div
            className="
              flex flex-wrap
              items-center gap-x-2 gap-y-1

              font-cairo
              text-xs font-medium
              leading-6

              text-[#9EB1C0]
            "
          >
            <span>
              © {currentYear}
            </span>

            <span>
              {content.eyebrow}.
            </span>

            <span>
              {content.copyright}.
            </span>
          </div>

          {/* Developer + Back to top */}
          <div
            className="
              flex flex-wrap
              items-center
              justify-between
              gap-5

              md:justify-end
              md:gap-8
            "
          >
            <div
              className="
                flex flex-wrap
                items-center gap-2

                font-cairo
                text-xs font-medium
                text-[#9EB1C0]
              "
            >
              <span>
                {content.development}
              </span>

              <a
                href="https://artcodey.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="
                  text-white/90
                  transition-colors duration-200
                  hover:text-[#D7B77F]
                "
              >
                Artcodey
              </a>
            </div>

            {/* Back to top */}
            <button
              type="button"
              onClick={handleBackToTop}
              aria-label={content.backToTop}
              title={content.backToTop}
              className="
                group flex h-10 w-10
                items-center justify-center

                rounded-xl
                border border-white/15
                bg-white/[0.06]

                text-white

                transition-all duration-300

                hover:-translate-y-1
                hover:border-white/30
                hover:bg-white/[0.12]

                focus-visible:outline-none
                focus-visible:ring-2
                focus-visible:ring-[#D7B77F]
              "
            >
              <HiOutlineArrowUp
                size={19}
                aria-hidden="true"
              />
            </button>

          </div>
        </div>

      </div>
    </footer>
  );
}

export default Fotter;
